import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_CONSTANTS } from "../../../API_CONSTANTS";
import { Spinner, Button, Card, Row, Col } from "react-bootstrap";
import Toast from "../../../Components/Toast/Toast";
import { faCheckCircle, faTimesCircle } from "@fortawesome/fontawesome-free-solid";

export default function SurgeryOverview() {
  const { procedureId } = useParams();
  const navigate = useNavigate();
  const [procedure, setProcedure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [toastIcon, setToastIcon] = useState(faCheckCircle);
  const [iconColor, setIconColor] = useState("");

  const handleToast = (message, icon, color, className) => {
    setToastMessage(message);
    setToastIcon(icon);
    setIconColor(color);
    setShowToast(`show ${className}`);
    setTimeout(() => setShowToast(""), 3000);
  };

  useEffect(() => {
    const fetchProcedureDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_CONSTANTS.GET_PROCEDURE_AND_SURGERY()}?limit=1000`, {
          headers: API_CONSTANTS.headers(),
        });
        
        const data = response.data?.response?.procedure_and_surgeries || [];
        const found = data.find(p => p.id === procedureId);
        
        if (found) {
          setProcedure(found);
        } else {
          handleToast("Procedure not found", faTimesCircle, "#dc3545", "show-error");
        }
      } catch (error) {
        handleToast("Error fetching details", faTimesCircle, "#dc3545", "show-error");
      } finally {
        setLoading(false);
      }
    };

    fetchProcedureDetails();
  }, [procedureId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!procedure) {
    return (
      <div className="p-4 text-center">
        <h4>Procedure not found</h4>
        <Button onClick={() => navigate("/surgery")}>Back to List</Button>
        <Toast cName={showToast} text={toastMessage} icon={toastIcon} color={iconColor} />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Procedure Details</h3>
        <Button variant="outline-secondary" onClick={() => navigate("/surgery")}>
          Back to List
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body>
          <Row className="mb-4">
            <Col md={6}>
              <h5 className="text-muted mb-3">General Information</h5>
              <div className="mb-2"><strong>Procedure Name:</strong> {procedure.procedure_name}</div>
              <div className="mb-2"><strong>Type:</strong> {procedure.procedure_type}</div>
              <div className="mb-2"><strong>ID:</strong> {procedure.id}</div>
              {procedure.procedure_image_url && (
                <div className="mb-2">
                  <strong>Image:</strong><br />
                  <img src={procedure.procedure_image_url} alt="Procedure" style={{ maxWidth: '200px', maxHeight: '150px' }} />
                </div>
              )}
            </Col>
            <Col md={6}>
              <h5 className="text-muted mb-3">Doctors ({procedure.doctors?.length || 0})</h5>
              {procedure.doctors && procedure.doctors.length > 0 ? (
                procedure.doctors.map((doctor, index) => (
                  <div key={doctor.doctor_id || index} className="mb-3 p-3 border rounded">
                    <div className="mb-2"><strong>Name:</strong> {doctor.name || doctor.doctor_name}</div>
                    <div className="mb-2"><strong>Specialization:</strong> {doctor.specialization || doctor.doctor_specialization}</div>
                    {doctor.experience && <div className="mb-2"><strong>Experience:</strong> {doctor.experience} years</div>}
                    {doctor.rating && <div className="mb-2"><strong>Rating:</strong> ⭐ {doctor.rating}</div>}
                  </div>
                ))
              ) : (
                <div>No doctor information available</div>
              )}
            </Col>
          </Row>

          {procedure.doctors && procedure.doctors.length > 0 && (
            <Row className="mb-4">
              <Col md={12}>
                <h5 className="text-muted mb-3">Clinic & Pricing Details</h5>
                {procedure.doctors.map((doctor, index) => (
                  <div key={doctor.doctor_id || index} className="mb-3 p-3 border rounded">
                    <h6>Doctor: {doctor.name || doctor.doctor_name}</h6>
                    <Row>
                      <Col md={6}>
                        <div className="mb-2"><strong>Clinic Name:</strong> {doctor.clinic?.name || doctor.clinic_name}</div>
                        <div className="mb-2"><strong>City:</strong> {doctor.clinic?.city || doctor.city}</div>
                        <div className="mb-2"><strong>Address:</strong> {doctor.clinic?.address || doctor.clinic_address}</div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-2"><strong>Price:</strong> {doctor.currency || procedure.currency || 'PKR'} {doctor.pricing}</div>
                        {doctor.image_url && (
                          <div className="mb-2">
                            <strong>Doctor Image:</strong><br />
                            <img src={doctor.image_url} alt="Doctor" style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '50%' }} />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                ))}
              </Col>
            </Row>
          )}

          <Row>
            <Col>
              <h5 className="text-muted mb-3">Description</h5>
              <p>{procedure.description}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Toast cName={showToast} text={toastMessage} icon={toastIcon} color={iconColor} />
    </div>
  );
}
